import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { TaskType } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { useEpisodeContext } from '@frontend/utils/hooks/useEpisodeContext';
import type { ICustomControlProcedureReadProps } from '../../interfaces/';
import { ProcedureReadButtons } from '../../../../elements/carePathButtons/ProcedureReadButtons';
import { FormParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { OphthalmologyDropzoneRead } from './OphthalmologyDropzoneRead';

export function TfoV1ProcedureRead(props: ICustomControlProcedureReadProps) {
    const { patient } = useEpisodeContext();
    const { isFirstItem, itemState, procedure, process, updateItemState } = props;
    const [diagnosticReportRemark, setDiagnosticReportRemark] = useState('');
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const isSupervisionOrganization =
        process.task?.performerOrganizationId === organizationId && process.task?.type === TaskType.supervise;

    useEffect(() => {
        if (process.diagnosticReports.length && isSupervisionOrganization) {
            loadDiagnosticReport(process.diagnosticReports[0].id);
        }
    }, []);

    return (
        <ProcessContainer isActiveActivity={itemState.initialIsOpen}>
            <ProcessItemHeader
                date={process.procedure && process.procedure.dateSent}
                isFirstItem={isFirstItem}
                isFirstProcess={itemState.isFirstProcess}
                isItemOpen={itemState.isOpen}
                onToggle={async (isOpened) =>
                    await updateItemState({
                        ...itemState,
                        isOpen: isOpened || itemState.initialIsOpen === true,
                    })
                }
                organizationName={
                    patient.authorization?.canSeePatientDetails && process.procedure?.performerOrganization?.name
                        ? process.procedure.performerOrganization.name
                        : undefined
                }
                practitionerName={
                    patient.authorization?.canSeePatientDetails && process.procedure?.performerPractitioner
                        ? getDisplayName(process.procedure.performerPractitioner)
                        : undefined
                }
                process={process}
                serviceType={process.serviceType}
                showProcessIcon
                title={getServiceLabel(process.code, 'procedure', process.name)}
            />

            <CustomControlCollapseBody isOpen={itemState.isOpen}>
                <OphthalmologyDropzoneRead
                    files={procedure.customControlData.files}
                    id={procedure.id}
                />
                <HorizontalFormGroupRead>
                    <FormLabel>{''}</FormLabel>

                    {(procedure.customControlData.visus_od ||
                        procedure.customControlData.oogdruk_od ||
                        procedure.customControlData.visus_os ||
                        procedure.customControlData.oogdruk_os) && (
                        <>
                            <InputContainer>
                                <FormLabel>OD</FormLabel>
                            </InputContainer>

                            <InputContainer>
                                <FormLabel>OS</FormLabel>
                            </InputContainer>
                        </>
                    )}
                </HorizontalFormGroupRead>

                {(procedure.customControlData.visus_od || procedure.customControlData.visus_os) && (
                    <>
                        <HorizontalFormGroupRead>
                            <FormLabel>Visus</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{procedure.customControlData.visus_od || ''}</FormParagraph>
                            </InputContainer>

                            <InputContainer>
                                <FormParagraph bold>{procedure.customControlData.visus_os || ''}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    </>
                )}

                {procedure.customControlData.hoe_gemeten && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Hoe gemeten</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{procedure.customControlData.hoe_gemeten}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {(procedure.customControlData.oogdruk_od || procedure.customControlData.oogdruk_os) && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Oogdruk</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{procedure.customControlData.oogdruk_od || ''}</FormParagraph>
                        </InputContainer>

                        <InputContainer>
                            <FormParagraph bold>{procedure.customControlData.oogdruk_os || ''}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {procedure.customControlData.patient_gedruppeld && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Patiënt gedruppeld</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{procedure.customControlData.patient_gedruppeld}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {procedure.remark && (
                    <HorizontalFormGroupRead>
                        <FormLabel>
                            {isSupervisionOrganization
                                ? translateKey('supervision.remarksProcedure')
                                : translateKey('common.remarks')}
                        </FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{procedure.remark}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {diagnosticReportRemark && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('diagnosticReport.remarksGrader')}</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{diagnosticReportRemark}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <ProcedureReadButtons {...props} />
            </CustomControlCollapseBody>

            {!itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={itemState.isOpen}
                    labelLess={translateKey('procedure.hideProcedure')}
                    labelMore={translateKey('procedure.showProcedure')}
                    onToggle={async (isOpened) => await updateItemState({ ...itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );

    async function loadDiagnosticReport(id: string) {
        const result = await fetchJson(`/api/diagnosticReports/${id}/`, 'GET');
        setDiagnosticReportRemark(result.json.customControlData?.toelichting);
    }
}
